// Portal do Cliente (0.26): troca de documentos com cada cliente (GED), nos dois sentidos.
// Cada cliente tem a sua pasta, ligada a um grupo próprio: o cliente entra pelo convite e só enxerga a pasta dele
// (as regras ficam no servidor, migracao-0.25.sql). Fotos e PDFs vão para a pasta do grupo e, se você ligou o
// Google Drive ou o OneDrive em Ajustes, também para o seu Drive.
// Texto 27 (B4): agora é a PASTA DO CLIENTE dentro da aba Escritório & Clientes: painel (pedidos, guias, honorário,
// recados), documentos por competência com situação (Novo · Visto · A pagar · Pago), a IA lê a guia (valor, vencimento,
// código de barras e Pix), comprovante anexado = Pago, pedidos com situação e repetição mensal, checklist de cliente novo,
// lembrete pelo WhatsApp, recados e a linha do tempo.
import { useEffect, useRef, useState } from 'react';
import { zipSync } from 'fflate';
import { Field, Chip } from '../components/ui';
import { bytesDoAnexo } from '../lib/fotosPdf';
import { montarPdf, nomeArquivo } from '../lib/pdf';
import { anexoDeBytes } from '../lib/anexos';
import { salvarECompartilhar } from '../lib/arquivoSalvar';
import { marcarAbaAtual } from '../lib/relatorios';
import { cancelarAviso, marcarAviso } from '../lib/meAvise';
import { getDoc, setDoc } from '../store/store';
import { semBloqueio } from '../auth/AuthContext';
import { Alert, Pressable, Text, View } from 'react-native';
import { router } from 'expo-router';
import { aoSalvarNaAba, tirarDaAba } from '../lib/clientes';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps, TopoProps } from './tipos';
import { dataBr, din, junta, op } from './util';
import { mudarCampos, novoReg, regPorId, regsDe, salvarReg } from '../store/modulos';
import { abrirSite, email as abrirEmail, whatsapp } from '../lib/mapa';
import { C, alpha } from '../components/theme';
import { addDays, nomeMes, today, uid } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { copiarTexto } from '../lib/copiar';
import { acessoDaFicha, grupoPorId, setSimulacao, useSimulacao } from '../lib/compartilhar';
import { gruposEmCache } from '../lib/grupos';
import { situacaoCliente } from '../lib/recebimentos';
import { Recolhivel } from '../components/Recolhivel';

const CATEGORIAS = [N_('Nota fiscal'), N_('Guia de imposto'), N_('Folha de pagamento'), N_('Extrato bancário'), N_('Comprovante'), N_('Contrato'),
  N_('Proposta'), N_('Recibo'), N_('Declaração'), N_('Certidão'), N_('Alvará'), N_('Balanço e relatórios'), N_('Documento pessoal'), N_('Outro')];
const SITUACOES = [N_('Novo'), N_('Visto'), N_('A pagar'), N_('Pago')];
const ehDoCliente = (r: Reg) => r.c.origem === 'Enviado pelo cliente';
const pendente = (r: Reg) => !r.c.entregue;
const comValidade = (c: Record<string, any>) => c.categoria === 'Certidão' || c.categoria === 'Alvará';
/** Texto 28 (C4): contratos e propostas têm o "Li e concordo" do cliente. */
const pedeAceite = (c: Record<string, any>) => c.categoria === 'Contrato' || c.categoria === 'Proposta';
const quandoBr = (iso: string) => { const d = new Date(iso); return `${dataBr(ymdLocal(d))} ${hm(iso)}`; };
const ymdLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const aPagar = (r: Reg) => !!r.c.vencimento && r.c.status !== 'Pago';
const atrasadoDoc = (r: Reg, hoje = today()) => aPagar(r) && r.c.vencimento < hoje;
const atrasadoPed = (r: Reg, hoje = today()) => pendente(r) && !!r.c.prazo && r.c.prazo < hoje;
/** "10/2026" ← "2026-10" e o contrário. */
const ym = (comp: unknown) => { const m = /^(\d{1,2})\/(\d{4})$/.exec(String(comp ?? '').trim()); return m ? `${m[2]}-${m[1].padStart(2, '0')}` : ''; };
/** Este mês primeiro, depois o mês passado e os 4 anteriores (o 1º e o 2º são os atalhos "Este mês" e "Mês passado"). */
const mesesRecentes = () => { const d = new Date(); return Array.from({ length: 6 }, (_, i) => { const x = new Date(d.getFullYear(), d.getMonth() - i, 1); return `${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`; }); };
const hm = (iso: string) => { const d = new Date(iso); return `${dataBr(iso.slice(0, 10)).slice(0, 5)}, ${String(d.getHours()).padStart(2, '0')}h${d.getMinutes() ? String(d.getMinutes()).padStart(2, '0') : ''}`; };
const baseDe = (pasta: Reg | null) => (pasta?.c.cid ? regPorId('clientes', pasta.c.cid) : null);

// ─── Pedido recorrente (B4.2): "todo mês, até o dia 5": a cada mês o app cria o pedido do mês (uma vez) ───
export function gerarPedidosDoMes(regs: Reg[] = regsDe('portal')) {
  const hoje = today(), mes = hoje.slice(0, 7);
  for (const p of regs.filter(r => r.col === 'pedido' && r.c.repetir && !r.c.daRepeticao)) {
    if (regs.some(r => r.col === 'pedido' && r.c.daRepeticao === p.id && r.c.mesRef === mes) || p.c.mesRef === mes) continue;
    const dia = Math.min(28, Math.max(1, Number(p.c.diaMes) || 5));
    salvarReg('portal', { ...novoReg('pedido', p.pai, { oque: p.c.oque, prazo: `${mes}-${String(dia).padStart(2, '0')}`, entregue: false, daRepeticao: p.id, mesRef: mes, notas: p.c.notas ?? '' }), g: p.g } as Reg);
  }
}


// ─── Texto 27 (sobras, 0.99) ──────────────────────────────────────────────────────────────────
/** "Baixar o mês": todos os arquivos dos documentos de uma competência num ZIP só (uma pasta por documento). */
async function baixarMes(chave: string, itens: Reg[], ctx: Ctx, pai: Reg | null) {
  const [a, m] = chave.split('-').map(Number);
  const mesTxt = m ? `${String(m).padStart(2, '0')}-${a}` : chave;
  const arquivos: Record<string, Uint8Array> = {};
  let faltou = 0, n = 0;
  const usados = new Set<string>();
  for (const d of itens) {
    const pasta = nomeArquivo(String(d.c.titulo || op(d.c.categoria) || t('Documento'))).slice(0, 60);
    for (const an of d.anexos ?? []) {
      if (!an.dado && !an.uri && !an.gp && !an.url) continue;
      if (an.url && !an.gp && !an.uri && !an.dado && !/^https:/i.test(an.url)) continue;
      const b = await bytesDoAnexo(an);
      if (!b) { faltou++; continue; }
      let nome = `${pasta}/${nomeArquivo(String(an.nome || 'arquivo'))}`;
      for (let i = 2; usados.has(nome); i++) nome = `${pasta}/${i}-${nomeArquivo(String(an.nome || 'arquivo'))}`;
      usados.add(nome); arquivos[nome] = b; n++;
    }
  }
  if (!n) return Alert.alert(t('Baixar o mês'), faltou ? t('Os arquivos deste mês estão em outro dispositivo e não puderam ser lidos aqui.') : t('Os documentos deste mês não têm arquivos.'));
  marcarAbaAtual('escritorio');
  const nomeZip = nomeArquivo(`${t('Documentos')} ${mesTxt} ${String(pai?.c.nome ?? '')}`.trim()) + '.zip';
  await semBloqueio(() => salvarECompartilhar(zipSync(arquivos, { level: 0 }), nomeZip, 'application/zip'));
  if (faltou) Alert.alert(t('Baixar o mês'), faltou === 1 ? t('1 arquivo está em outro dispositivo e ficou de fora.') : t('{n} arquivos estão em outro dispositivo e ficaram de fora.', { n: faltou }));
}

/** Conversa presa a um documento (pergunta e resposta sobre ele), visível para o escritório e para o cliente. */
type MsgDoc = { id: string; de: 'escritorio' | 'cliente'; texto: string; em: string };
function ConversaDoc({ r, ctx, cor, soVer }: { r: Reg; ctx: Ctx; cor: string; soVer: boolean }) {
  const [txt, setTxt] = useState('');
  const msgs: MsgDoc[] = Array.isArray(r.c.conversa) ? r.c.conversa : [];
  const souCliente = !!r.g && !acessoDaFicha(r).admin;
  const podeEscrever = !soVer || (souCliente && acessoDaFicha(r).editar('documento'));
  const enviar = () => {
    const texto = txt.trim(); if (!texto) return;
    mudarCampos(ctx.mod, r.id, { conversa: [...msgs, { id: uid(), de: souCliente ? 'cliente' : 'escritorio', texto: texto.slice(0, 1000), em: new Date().toISOString() }] });
    setTxt('');
  };
  return (
    <Recolhivel visual="cartao" chave={`portal:doc:${r.id}:conversa`} titulo={'💬 ' + t('Conversa sobre este documento')} resumo={msgs.length ? String(msgs.length) : ''} cor={cor}>
      {!msgs.length && <Text style={{ color: C.mute, fontSize: 13 }}>{t('Pergunte ou responda algo sobre este documento. O escritório e o cliente veem.')}</Text>}
      {msgs.map(m => (
        <View key={m.id} style={{ alignSelf: m.de === 'cliente' ? 'flex-start' : 'flex-end', maxWidth: '88%', backgroundColor: m.de === 'cliente' ? C.card2 : alpha(cor, 0.18), borderRadius: 12, padding: 8 }}>
          <Text style={{ color: C.sub, fontSize: 10 }}>{m.de === 'cliente' ? t('Cliente') : t('Escritório')} · {hm(m.em)}</Text>
          <Text style={{ color: C.text }}>{m.texto}</Text>
        </View>
      ))}
      {podeEscrever && (
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-end' }}>
          <Field value={txt} onChangeText={setTxt} placeholder={t('Escreva a mensagem')} multiline maxLength={1000} style={{ flex: 1 }} />
          <Pressable onPress={enviar} disabled={!txt.trim()} style={{ backgroundColor: txt.trim() ? cor : C.card2, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 10 }}>
            <Text style={{ color: txt.trim() ? '#fff' : C.mute, fontWeight: '700' }}>{t('Enviar')}</Text></Pressable>
        </View>
      )}
    </Recolhivel>
  );
}

/** Modelos de pedido: guardados neste aparelho (Ajustes), para reusar com outros clientes. */
type ModeloPedido = { oque: string; notas?: string; repetir?: boolean; diaMes?: number };
const modelosPedido = (): ModeloPedido[] => ((getDoc('ajustes') as any).modelosPedido ?? []) as ModeloPedido[];
function guardarModelo(m: ModeloPedido) {
  const atuais = modelosPedido().filter(x => x.oque.trim().toLowerCase() !== m.oque.trim().toLowerCase());
  setDoc('ajustes', d => ({ ...d, modelosPedido: [m, ...atuais].slice(0, 40) }) as any);
}
function ModelosPedido({ c, mudar, cor }: { c: Record<string, any>; mudar: (p: Record<string, any>) => void; cor: string }) {
  const [ver, setVer] = useState(false);
  const lista = modelosPedido();
  if (!lista.length || c.oque) return null;
  return (
    <View style={{ gap: 6 }}>
      <Pressable onPress={() => setVer(!ver)}><Text style={{ color: cor, fontWeight: '700', fontSize: 13 }}>{ver ? '▾' : '▸'} 📋 {t('Usar um modelo de pedido ({n})', { n: lista.length })}</Text></Pressable>
      {ver && (
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {lista.map(m => <Chip key={m.oque} small cor={cor} on={false} label={m.oque} onPress={() => mudar({ oque: m.oque, notas: m.notas ?? '', repetir: !!m.repetir, diaMes: m.diaMes ?? '' })} />)}
        </View>
      )}
    </View>
  );
}

/** Lembrete automático do pedido: o app avisa VOCÊ para lembrar o cliente pelo WhatsApp (1, 2 ou 3 vezes antes do prazo).
 *  Só para clientes que autorizaram mensagens. Os avisos ficam neste aparelho. */
const DIAS_LEMBRETE: Record<string, number[]> = { '1': [1], '2': [3, 1], '3': [5, 3, 1] };
async function agendarLembretes(r: Reg, ctx: Ctx) {
  for (let i = 0; i < 3; i++) await cancelarAviso({ aba: 'portal', id: `${r.id}#l${i}` });
  const n = String(r.c.lembretes || '');
  if (!DIAS_LEMBRETE[n] || r.c.entregue || !r.c.prazo) return;
  const pasta = ctx.porId(r.pai), base = baseDe(pasta);
  if (base && !base.c.autoriza) return;
  const nome = String(base?.c.apelido || pasta?.c.contato || pasta?.c.nome || '').split(' ')[0];
  let i = 0;
  for (const d of DIAS_LEMBRETE[n]) {
    const quando = new Date(addDays(String(r.c.prazo), -d) + 'T09:00:00');
    if (quando.getTime() > Date.now()) await marcarAviso({ aba: 'portal', id: `${r.id}#l${i}`, item: r.pai ?? r.id,
      titulo: t('Lembrar {nome} pelo WhatsApp: {o}', { nome, o: String(r.c.oque || '') }) }, quando);
    i++;
  }
}

/** Recibo do honorário: PDF que já entra na pasta do cliente como documento do escritório. */
async function reciboHonorario(pasta: Reg, ctx: Ctx, comp: string) {
  const base = baseDe(pasta);
  const valor = Number(base?.c.honorario) || 0;
  if (!valor) return Alert.alert(t('Sem honorário'), t('Cadastre o valor do honorário no bloco "Escritório" da ficha do cliente, em Meus clientes.'));
  const conf = (getDoc('ajustes') as any).recebimentos ?? {};
  const quem = String(conf.nomeRecibo || gruposEmCache().lista?.find(g => g.aba === 'escritorio')?.nome || t('Escritório'));
  const blocos: BlocoPdfX[] = [
    { t: 'titulo', texto: t('Recibo') },
    { t: 'par', texto: t('Recebi de {c} a quantia de {v}, referente aos honorários da competência {m}.', { c: String(pasta.c.nome ?? ''), v: din(valor, ctx.moeda), m: comp }) },
    { t: 'par', texto: dataBr(today()) },
    { t: 'espaco', h: 40 },
    { t: 'assinatura', linhas: [quem] },
  ];
  const bytes = await montarPdf(blocos as any, quem);
  const nome = nomeArquivo(t('Recibo honorário {m} {c}', { m: comp.replace('/', '-'), c: String(pasta.c.nome ?? '') })) + '.pdf';
  const an = await anexoDeBytes(nome, bytes, 'application/pdf');
  salvarReg(ctx.mod, { ...novoReg('documento', pasta.id, { titulo: t('Recibo do honorário {m}', { m: comp }), categoria: 'Recibo', origem: 'Enviado pelo escritório',
    competencia: comp, data: today(), status: 'Novo' }), anexos: [an], g: pasta.g } as Reg);
  Alert.alert(t('Recibo criado'), t('O recibo entrou em Documentos, na competência {m}.', { m: comp }));
}
type BlocoPdfX = { t: string; [k: string]: any };

// ─── Checklist de cliente novo (B4.2): documentos iniciais, prontos por tipo ───
const CHECKLIST: Record<string, string[]> = {
  [N_('Pessoa física')]: [N_('RG e CPF'), N_('Comprovante de endereço'), N_('Última declaração do Imposto de Renda'), N_('Informes de rendimentos'), N_('Dados bancários')],
  [N_('MEI')]: [N_('CCMEI (certificado do MEI)'), N_('RG e CPF do titular'), N_('Comprovante de endereço'), N_('Acesso ao gov.br'), N_('Notas fiscais emitidas')],
  [N_('Empresa')]: [N_('Contrato social e alterações'), N_('Cartão CNPJ'), N_('RG e CPF dos sócios'), N_('Certificado digital'), N_('Extratos bancários'), N_('Últimas guias pagas'), N_('Folha de pagamento')],
};

/** Texto 27 (B4): o topo da pasta do cliente: botões, painel (Pedidos · Guias · Honorário · Recados) e a linha do tempo. */
function PastaTopo({ r, ctx }: TopoProps) {
  const docs = ctx.filhos(r.id, 'documento'), pedidos = ctx.filhos(r.id, 'pedido'), recados = ctx.filhos(r.id, 'recado');
  useEffect(() => { try { gerarPedidosDoMes(ctx.regs); } catch { /* segue */ } }, [r.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const base = baseDe(r);
  const hon = base ? situacaoCliente(base) : null;
  const abertos = pedidos.filter(pendente), atras = pedidos.filter(p => atrasadoPed(p));
  const guias = docs.filter(aPagar), guiasAtras = docs.filter(d => atrasadoDoc(d));
  const recNovos = recados.filter(x => !x.c.lido && x.c.de === 'cliente');
  const quadro = (k: string, rot: string, v: string, cor?: string, sub?: string) => (
    <View key={k} style={{ flexBasis: '47%', flexGrow: 1, backgroundColor: C.card, borderRadius: 12, padding: 10 }}>
      <Text style={{ color: C.sub, fontSize: 11 }}>{rot}</Text>
      <Text style={{ color: cor ?? C.text, fontWeight: '800', fontSize: 15 }}>{v}</Text>
      {!!sub && <Text style={{ color: C.mute, fontSize: 10 }}>{sub}</Text>}
    </View>);
  const eventos = [
    ...docs.map(d => ({ d: String(d.c.data || d.criado.slice(0, 10)), txt: (ehDoCliente(d) ? '📥 ' + t('{n} enviou {doc}', { n: String(r.c.nome || t('O cliente')).split(' ')[0], doc: String(d.c.titulo || op(d.c.categoria) || t('documento')) })
      : '📤 ' + t('Escritório enviou {doc}', { doc: String(d.c.titulo || op(d.c.categoria) || t('documento')) })) })),
    ...docs.filter(d => d.c.vistoClienteEm).map(d => ({ d: String(d.c.vistoClienteEm).slice(0, 10), txt: '👁️ ' + t('Cliente viu {doc}', { doc: String(d.c.titulo || t('documento')) }) })),
    ...docs.filter(d => d.c.pagoEm).map(d => ({ d: String(d.c.pagoEm), txt: '✅ ' + t('{doc} pago', { doc: String(d.c.titulo || t('Guia')) }) })),
    ...pedidos.map(p => ({ d: p.criado.slice(0, 10), txt: '📌 ' + t('Pedido feito: {o}', { o: String(p.c.oque || '') }) })),
    ...pedidos.filter(p => p.c.recebidoEm).map(p => ({ d: String(p.c.recebidoEm), txt: '📬 ' + t('Pedido recebido: {o}', { o: String(p.c.oque || '') }) })),
    ...recados.map(x => ({ d: x.criado.slice(0, 10), txt: '💬 ' + String(x.c.texto || '').slice(0, 60) })),
  ].sort((a, b) => b.d.localeCompare(a.d));
  const lembrar = () => {
    if (!r.c.telefone) return Alert.alert(t('Sem telefone'), t('Cadastre o telefone do cliente para lembrar pelo WhatsApp.'));
    if (base && !base.c.autoriza) return Alert.alert(t('Sem autorização'), t('Este cliente não autorizou receber mensagens (ficha em Meus clientes).'));
    const nome = String(base?.c.apelido || r.c.contato || r.c.nome || '').split(' ')[0];
    whatsapp(String(r.c.telefone), abertos.length
      ? t('Oi, {nome}! Faltam: {itens}. Pode mandar por aqui?', { nome, itens: abertos.map(p => String(p.c.oque)).join(', ') })
      : t('Oi, {nome}! Tudo certo por aí?', { nome }));
  };
  // texto 27 (B4.6): "Ver como o cliente": a pasta com o papel do cliente, só olhando (sai ao fechar a pasta)
  const sim = useSimulacao();
  const vendoComoCliente = !!r.g && sim?.grupo === r.g;
  const souAdmin = !!r.g && !!grupoPorId(r.g)?.admin;
  useEffect(() => () => { if (simulandoEste.current) setSimulacao(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const simulandoEste = useRef(false); simulandoEste.current = vendoComoCliente;
  if (vendoComoCliente) return (
    <View style={{ backgroundColor: alpha(ctx.cor, 0.18), borderRadius: 14, padding: 12, gap: 6, borderWidth: 1, borderColor: ctx.cor }}>
      <Text style={{ color: C.text, fontWeight: '800' }}>👁 {t('Vendo como o cliente')}</Text>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('É assim que o cliente vê a pasta dele: sem as anotações internas e sem o que é só da equipe. Nada pode ser mudado neste modo.')}</Text>
      <Pressable onPress={() => setSimulacao(null)} style={{ alignSelf: 'flex-start', backgroundColor: ctx.cor, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 8 }}>
        <Text style={{ color: '#fff', fontWeight: '700' }}>{t('Voltar a ver como escritório')}</Text></Pressable>
    </View>
  );
  return (
    <View style={{ gap: 8 }}>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        {souAdmin && <Pressable onPress={() => setSimulacao({ grupo: r.g!, papel: 'cliente' })} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 14, backgroundColor: C.card2 }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>👁 {t('Ver como o cliente')}</Text></Pressable>}
        {!!base && <Pressable onPress={() => router.push({ pathname: '/aba/[id]', params: { id: 'clientes', item: base.id } } as any)} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 14, backgroundColor: alpha(ctx.cor, 0.15) }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>👤 {t('Ficha completa')} ›</Text></Pressable>}
        {abertos.length > 0 && !!r.c.telefone && <Pressable onPress={lembrar} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 14, backgroundColor: alpha('#16A34A', 0.15) }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 13 }}>💬 {t('Lembrar pelo WhatsApp')}</Text></Pressable>}
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {quadro('p', '📌 ' + t('Pedidos pendentes'), String(abertos.length), atras.length ? C.danger : undefined, atras.length ? t('{n} atrasado(s)', { n: atras.length }) : undefined)}
        {quadro('g', '🧾 ' + t('Guias a pagar'), String(guias.length), guiasAtras.length ? C.danger : undefined, guiasAtras.length ? t('{n} vencida(s)', { n: guiasAtras.length }) : undefined)}
        {quadro('h', '💰 ' + t('Honorário'), base?.c.honorario ? din(base.c.honorario, ctx.moeda) : '—', hon?.atrasadas.length ? C.danger : undefined,
          hon ? junta(hon.deve > 0 && t('a receber {v}', { v: din(hon.deve, ctx.moeda) }), hon.atrasadas.length && t('atrasado')) || undefined : undefined)}
        {quadro('r', '💬 ' + t('Recados'), String(recados.length), recNovos.length ? ctx.cor : undefined, recNovos.length ? t('{n} novo(s)', { n: recNovos.length }) : undefined)}
      </View>
      <Recolhivel visual="cartao" chave={`portal:${r.id}:tempo`} titulo={'🕒 ' + t('Linha do tempo')} resumo={eventos.length ? String(eventos.length) : ''}>
        {!eventos.length && <Text style={{ color: C.mute, fontSize: 13 }}>{t('Nada ainda.')}</Text>}
        {eventos.slice(0, 60).map((e, i) => <Text key={i} style={{ color: C.text, fontSize: 13 }}><Text style={{ color: C.sub }}>{dataBr(e.d).slice(0, 5)} · </Text>{e.txt}</Text>)}
      </Recolhivel>
    </View>
  );
}

function PainelCliente({ ctx, pai }: PainelProps) {
  if (!pai || pai.col !== 'cliente') return null;
  const docs = ctx.filhos(pai.id, 'documento'), pedidos = ctx.filhos(pai.id, 'pedido');
  const novosDoCliente = docs.filter(d => ehDoCliente(d) && d.c.status === 'Novo');
  const abertos = pedidos.filter(pendente);
  const aVencer = docs.filter(aPagar);
  if (!docs.length && !pedidos.length) return null;
  return (
    <View style={{ backgroundColor: alpha(ctx.cor, 0.1), borderRadius: 14, padding: 12, gap: 4 }}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{junta(t('{n} documento(s)', { n: docs.length }), novosDoCliente.length && t('{n} novo(s) do cliente', { n: novosDoCliente.length }),
        abertos.length && t('{n} pedido(s) em aberto', { n: abertos.length }), aVencer.length && t('{n} a pagar', { n: aVencer.length }))}</Text>
      {!!pai.c.pasta && <Pressable onPress={() => abrirSite(pai.c.pasta)}><Text style={{ color: ctx.cor, fontWeight: '700', fontSize: 13 }}>☁️ {t('Abrir a pasta no Drive')}</Text></Pressable>}
    </View>
  );
}

const cliente: Colecao = {
  id: 'cliente', nome: N_('Clientes'), um: N_('Cliente'), icone: '🗂️',
  campos: [
    { k: 'nome', r: N_('Nome ou razão social'), t: 'texto' },
    { k: 'documento', r: N_('CNPJ ou CPF'), t: 'doc' },   // texto 25 (C4): mascarado; ver completo pede a senha
    { k: 'contato', r: N_('Pessoa de contato'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'email', r: N_('E-mail'), t: 'email' },
    { k: 'pasta', r: N_('Link da pasta no Drive ou OneDrive (opcional)'), t: 'link' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
    // texto 27 (B4.7): anotações internas, só a equipe vê. Ficam num pedaço da ficha que o cliente não recebe (servidor).
    { k: 'internas', r: N_('🔒 Anotações internas (o cliente nunca vê)'), t: 'longo', seg: 'clin' },
  ],
  // texto 25 (C4): o CPF/CNPJ não aparece mais no subtítulo; a ficha fica ligada a Meus clientes
  titulo: r => r.c.nome || '', sub: r => junta(r.c.contato),
  ordem: 'titulo', painelTopo: PastaTopo,
  aoSalvar: (r, ctx) => aoSalvarNaAba(ctx.mod, r), aoExcluir: (r, ctx) => tirarDaAba(ctx.mod, r),
  acoes: (r, ctx, abrirMenu) => [
    ...(r.c.telefone ? [{ label: '💬 WhatsApp', onPress: () => whatsapp(r.c.telefone, '') }] : []),
    ...(r.c.email ? [{ label: '✉️ ' + t('E-mail'), onPress: () => abrirEmail(r.c.email) }] : []),
    // texto 27 (B4.3): recibo do honorário, que já entra na pasta (o cliente vê)
    ...(acessoDaFicha(r).admin ? [{ label: '🧾 ' + t('Recibo do honorário…'), onPress: () => abrirMenu?.(t('Competência do recibo'), mesesRecentes().map(m => ({ label: m,
      onPress: () => { reciboHonorario(r, ctx, m).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e))); } }))) }] : []),
    // B4.2: checklist de cliente novo (documentos iniciais, por tipo; depois dá para editar cada pedido)
    { label: '📋 ' + t('Checklist de cliente novo'), onPress: () => abrirMenu?.(t('Que tipo de cliente?'), Object.keys(CHECKLIST).map(tp => ({ label: t(tp), onPress: () => {
      const ja = new Set(ctx.filhos(r.id, 'pedido').map(p => String(p.c.oque)));
      const novos = CHECKLIST[tp].map(x => t(x)).filter(x => !ja.has(x));
      for (const x of novos) salvarReg(ctx.mod, { ...novoReg('pedido', r.id, { oque: x, prazo: addDays(today(), 7), entregue: false }), g: r.g } as Reg);
      Alert.alert(t('Checklist criado'), t('{n} pedido(s) de documentos criados, com prazo de 7 dias.', { n: novos.length }));
    } }))) },
  ],
  vazio: N_('Crie a pasta de cada cliente, convide o cliente pelo link (ele só vê a pasta dele) e troquem documentos nos dois sentidos: notas, guias, folha, extratos, contratos.'),
};

const documento: Colecao = {
  id: 'documento', pai: 'cliente', nome: N_('Documentos'), um: N_('Documento'), anexos: true, marcas: [N_('Comprovante')], rapido: 'status',
  porFoto: N_('Ler a guia ou o boleto com a minha IA'),
  ordem: (a, b) => String(b.c.data ?? '').localeCompare(String(a.c.data ?? '')),
  icone: r => (ehDoCliente(r) ? '📥' : '📤'),
  campos: [
    { k: 'titulo', r: N_('Título'), t: 'texto', ph: N_('Ex.: DAS de setembro, Extrato do Itaú') },
    { k: 'categoria', r: N_('Categoria'), t: 'opcao', op: CATEGORIAS },
    { k: 'origem', r: N_('Quem enviou'), t: 'opcao', op: [N_('Enviado pelo escritório'), N_('Enviado pelo cliente')] },
    // B4.1: competência vira seletor de mês e ano (o 1º é este mês, o 2º o mês passado); o que já estava escrito continua
    { k: 'competencia', r: N_('Competência (mês de referência)'), t: 'opcao', opDe: (_ctx, _p, c) => [...new Set([...mesesRecentes(), ...(c?.competencia ? [String(c.competencia)] : [])])] },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'vencimento', r: N_('Vencimento (guias e boletos)'), t: 'data', aviso: [3, 0], ia: N_('data de vencimento') },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro', mostrar: c => !!c.vencimento || c.categoria === 'Guia de imposto', ia: N_('valor a pagar') },
    { k: 'codigo', r: N_('Código de barras (linha digitável)'), t: 'texto', mostrar: c => !!c.vencimento || !!c.codigo, ia: N_('linha digitável do boleto ou da guia, só números') },
    { k: 'pix', r: N_('Pix copia e cola'), t: 'texto', mostrar: c => !!c.vencimento || !!c.pix, ia: N_('código Pix copia e cola, se houver') },
    // "Pago" e "A pagar" só quando o documento tem vencimento (guias e boletos)
    { k: 'status', r: N_('Situação'), t: 'opcao', op: SITUACOES, traduzir: true, opDe: (_ctx, _p, c) => (c?.vencimento ? SITUACOES : SITUACOES.slice(0, 2)) },
    { k: 'validade', r: N_('Válido até'), t: 'data', mostrar: comValidade, aviso: [30, 10], semHoje: true },
    { k: 'pedidoDe', r: N_('Responde ao pedido'), t: 'opcao', opDe: (ctx, pai, c) => [...ctx.filhos(pai?.id ?? '', 'pedido').filter(p => pendente(p) || p.c.oque === c?.pedidoDe).map(p => String(p.c.oque))],
      semOpcoes: N_('Nenhum pedido em aberto.') },
    { k: 'notas', r: N_('Recado'), t: 'longo' },
    // texto 28 (C4): documento sensível pede a senha do app para abrir (na equipe e no cliente)
    { k: 'sensivel', r: N_('Documento sensível (pede a senha do app para abrir)'), t: 'sim', interruptor: true },
  ],
  titulo: r => (r.c.sensivel ? '🔒 ' : '') + (r.c.titulo || op(r.c.categoria) || t('Documento')),
  sub: r => junta(op(r.c.categoria), ehDoCliente(r) ? t('do cliente') : t('do escritório'),
    aPagar(r) && (atrasadoDoc(r) ? '🔴 ' + t('venceu {d}', { d: dataBr(r.c.vencimento).slice(0, 5) }) : '📅 ' + t('vence {d}', { d: dataBr(r.c.vencimento).slice(0, 5) })),
    r.c.valor && din(r.c.valor, 'BRL'), r.c.pagoEm && '✅ ' + t('pago em {d}', { d: dataBr(r.c.pagoEm).slice(0, 5) }),
    comValidade(r.c) && r.c.validade && t('válido até {d}', { d: dataBr(r.c.validade) }),
    r.c.vistoClienteEm && '👁️ ' + t('Visto pelo cliente em {d}', { d: hm(String(r.c.vistoClienteEm)) }),
    r.c.aceiteEm && '✍️ ' + t('Li e concordo: {d}', { d: quandoBr(String(r.c.aceiteEm)) }),
    Array.isArray(r.c.conversa) && r.c.conversa.length && '💬 ' + r.c.conversa.length),
  pedirSenha: r => !!r.c.sensivel,
  feito: r => r.c.status === 'Pago',
  novo: () => ({ data: today(), status: 'Novo', origem: 'Enviado pelo escritório', competencia: mesesRecentes()[1] }),
  // B4.1: agrupados por competência ("Outubro / 2026 (3)"), o mais recente em cima
  grupoDe: r => ym(r.c.competencia) || String(r.c.data || r.criado).slice(0, 7),
  rotuloGrupo: (k, itens) => { const [a, m] = k.split('-').map(Number); return `${m ? nomeMes(m - 1) : k} / ${a || ''} (${itens.length})`; },
  filtros: [[N_('Do cliente'), r => ehDoCliente(r)], [N_('Do escritório'), r => !ehDoCliente(r)], [N_('Novos'), r => r.c.status === 'Novo'],
    [N_('A pagar'), aPagar], [N_('Atrasados'), r => atrasadoDoc(r)]],
  contarZero: true,
  // texto 27 (sobras): meses antigos recolhidos, "Baixar o mês", várias fotos num PDF e a conversa sobre o documento
  gruposFechados: true, juntarFotos: true, fimFicha: ConversaDoc,
  acaoGrupo: { rotulo: N_('⬇️ Baixar o mês'), fn: (k, itens, ctx, pai) => { baixarMes(k, itens, ctx, pai).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e))); } },
  acoes: (r, ctx) => [
    // texto 28 (C4): "Li e concordo" do cliente em contratos e propostas (aceite simples: guarda data e hora)
    ...(pedeAceite(r.c) && !ehDoCliente(r) && !r.c.aceiteEm && r.g && !acessoDaFicha(r).admin && acessoDaFicha(r).editar('documento') ? [{ label: '✍️ ' + t('Li e concordo'), onPress: () => {
      Alert.alert(t('Li e concordo'), t('Ao tocar em Concordo, o app guarda a data e a hora em que você leu e concordou com "{doc}". É um aceite simples: não substitui uma assinatura digital oficial.', { doc: String(r.c.titulo || op(r.c.categoria)) }), [
        { text: t('Cancelar'), style: 'cancel' },
        { text: t('Concordo'), onPress: () => mudarCampos(ctx.mod, r.id, { aceiteEm: new Date().toISOString() }) }]);
    } }] : []),
    ...(r.c.codigo ? [{ label: '📋 ' + t('Copiar código de barras'), onPress: () => { copiarTexto(String(r.c.codigo).replace(/\s/g, '')).then(ok => ok && Alert.alert(t('Copiado'), t('Cole no app do banco.'))); } }] : []),
    ...(r.c.pix ? [{ label: '💠 ' + t('Copiar Pix'), onPress: () => { copiarTexto(String(r.c.pix).trim()).then(ok => ok && Alert.alert(t('Copiado'), t('Cole no app do banco, em Pix copia e cola.'))); } }] : []),
    ...(r.c.status === 'Novo' ? [{ label: '👁️ ' + t('Marcar como visto'), onPress: () => mudarCampos(ctx.mod, r.id, { status: 'Visto' }) }] : []),
    // B4.1: certidões e alvarás: "vence em 10 dias. Pedir nova?"
    ...(comValidade(r.c) && r.c.validade ? [{ label: '🔁 ' + t('Pedir nova ({c})', { c: t(op(r.c.categoria)) }), onPress: () => {
      salvarReg(ctx.mod, { ...novoReg('pedido', r.pai, { oque: t('{c} nova', { c: t(op(r.c.categoria)) }), prazo: r.c.validade, entregue: false }), g: r.g } as Reg);
      Alert.alert(t('Pedido criado'), t('O pedido entrou em Pedidos de documentos.'));
    } }] : []),
  ],
  // B4.1: quem abre o documento do outro lado fica registrado ("Visto pelo cliente em 23/10, às 14h")
  aoAbrir: (r, ctx) => {
    const ac = acessoDaFicha(r);
    if (!r.g || ac.admin) { if (ehDoCliente(r) && r.c.status === 'Novo') mudarCampos(ctx.mod, r.id, { status: 'Visto' }); return; }
    if (!ehDoCliente(r) && !r.c.vistoClienteEm && ac.editar('documento')) mudarCampos(ctx.mod, r.id, { vistoClienteEm: new Date().toISOString(), ...(r.c.status === 'Novo' ? { status: 'Visto' } : {}) });
  },
  aoSalvar: (r, ctx) => {
    const muda: Record<string, any> = {};
    // B4.1: o cliente anexou o COMPROVANTE: a guia vira "Pago" sozinha, com a data
    if (r.c.status !== 'Pago' && r.c.vencimento && (r.anexos ?? []).some(a => String(a.nome ?? '').startsWith(t('Comprovante') + ' · '))) Object.assign(muda, { status: 'Pago', pagoEm: today() });
    if (r.c.status === 'Pago' && !r.c.pagoEm) muda.pagoEm = today();
    if (Object.keys(muda).length) mudarCampos(ctx.mod, r.id, muda);
    // B4.2: chegou o documento pedido: o pedido fecha sozinho
    if (r.c.pedidoDe) for (const p of ctx.filhos(r.pai ?? '', 'pedido').filter(p => pendente(p) && p.c.oque === r.c.pedidoDe)) mudarCampos(ctx.mod, p.id, { entregue: true, recebidoEm: today(), docId: r.id });
  },
  painel: PainelCliente,
  vazio: N_('Envie guias, notas e relatórios para o cliente, ou veja o que ele mandou. Cada documento aceita fotos, PDFs e links.'),
};

const situacaoPed = (r: Reg) => (r.c.entregue ? t('Recebido') : atrasadoPed(r) ? t('Atrasado') : t('Pendente'));
const pedido: Colecao = {
  id: 'pedido', pai: 'cliente', nome: N_('Pedidos de documentos'), um: N_('Pedido'), check: 'entregue', data: 'prazo', anexos: true,
  icone: r => (r.c.entregue ? '✅' : atrasadoPed(r) ? '🔴' : '📌'),
  campos: [
    { k: 'oque', r: N_('O que precisa'), t: 'texto', ph: N_('Ex.: Notas de compra de setembro') },
    { k: 'prazo', r: N_('Prazo'), t: 'data', aviso: [2, 0] },
    { k: 'entregue', r: N_('Recebido'), t: 'sim', interruptor: true },
    // B4.2: pedido recorrente ("todo mês, até o dia 5: extrato e notas fiscais")
    { k: 'repetir', r: N_('Pedir de novo todo mês'), t: 'sim', interruptor: true, mostrar: c => !c.daRepeticao },
    { k: 'diaMes', r: N_('Até o dia (do mês)'), t: 'numero', mostrar: c => !!c.repetir && !c.daRepeticao },
    // texto 27 (B4.2): lembrete automático (o app me avisa para lembrar o cliente; só quem autorizou mensagens)
    { k: 'lembretes', r: N_('Me avisar para lembrar o cliente'), t: 'opcao', op: [N_('Não'), '1', '2', '3'], mostrar: c => !!c.prazo && !c.entregue },
    { k: 'notas', r: N_('Detalhes'), t: 'longo' },
  ],
  extraFicha: ModelosPedido,
  titulo: r => r.c.oque || '',
  sub: r => junta(situacaoPed(r), r.c.prazo && t('até {d}', { d: dataBr(r.c.prazo) }), r.c.repetir && '🔁 ' + t('todo mês, até o dia {n}', { n: Number(r.c.diaMes) || 5 }), r.c.recebidoEm && t('recebido em {d}', { d: dataBr(r.c.recebidoEm) })),
  feito: r => !!r.c.entregue,
  filtros: [[N_('Pendentes'), r => pendente(r) && !atrasadoPed(r)], [N_('Atrasados'), r => atrasadoPed(r)], [N_('Recebidos'), r => !!r.c.entregue]], contarZero: true,
  aoSalvar: (r, ctx) => {
    if (r.c.entregue && !r.c.recebidoEm) mudarCampos(ctx.mod, r.id, { recebidoEm: today() });
    if (r.c.repetir && !r.c.daRepeticao && !r.c.mesRef) mudarCampos(ctx.mod, r.id, { mesRef: String(r.c.prazo || today()).slice(0, 7) });
    if (!r.g || acessoDaFicha(r).admin) agendarLembretes(r, ctx).catch(() => {});
  },
  acoes: (r, ctx) => {
    const pasta = ctx.porId(r.pai);
    const modelo = { label: '💾 ' + t('Guardar como modelo'), onPress: () => { guardarModelo({ oque: String(r.c.oque || ''), notas: String(r.c.notas || ''), repetir: !!r.c.repetir, diaMes: Number(r.c.diaMes) || undefined });
      Alert.alert(t('Modelo guardado'), t('Ao criar um pedido para qualquer cliente, toque em "Usar um modelo de pedido".')); } };
    return [...(r.c.oque ? [modelo] : []), ...(pasta?.c.telefone && pendente(r) ? [{ label: '💬 ' + t('Lembrar pelo WhatsApp'), onPress: () => {
      const base = baseDe(pasta);
      if (base && !base.c.autoriza) return Alert.alert(t('Sem autorização'), t('Este cliente não autorizou receber mensagens (ficha em Meus clientes).'));
      whatsapp(String(pasta.c.telefone), t('Oi, {nome}! Falta: {itens}. Pode mandar por aqui?', { nome: String(base?.c.apelido || pasta.c.contato || pasta.c.nome || '').split(' ')[0], itens: String(r.c.oque) }));
    } }] : [])];
  },
  agenda: r => (r.c.prazo ? { start: r.c.prazo, title: t('Documento: {o}', { o: r.c.oque }) } : null),
  vazio: N_('Peça documentos ao cliente com prazo. Ele recebe, anexa e marca como entregue.'),
};

// B4.4: conversa com o cliente (recados), com marcação de novo
const recado: Colecao = {
  id: 'recado', pai: 'cliente', nome: N_('Recados'), um: N_('Recado'), icone: r => (r.c.de === 'cliente' ? (r.c.lido ? '💬' : '🟣') : '🗨️'),
  campos: [
    { k: 'texto', r: N_('Recado'), t: 'longo', simples: true, obrigatorio: N_('Escreva o recado.') },
    { k: 'de', r: N_('De'), t: 'opcao', op: ['escritorio', 'cliente'], mostrar: () => false },
  ],
  titulo: r => String(r.c.texto || '').split('\n')[0].slice(0, 80),
  sub: r => junta(r.c.de === 'cliente' ? t('do cliente') : t('do escritório'), r.c.de === 'cliente' && !r.c.lido && t('novo'), dataBr(r.criado.slice(0, 10))),
  ordem: (a, b) => b.criado.localeCompare(a.criado),
  novo: (_ctx, pai) => ({ de: pai && pai.g && !acessoDaFicha(pai).admin ? 'cliente' : 'escritorio' }),
  aoAbrir: (r, ctx) => { if (r.c.de === 'cliente' && !r.c.lido && (!r.g || acessoDaFicha(r).admin)) mudarCampos(ctx.mod, r.id, { lido: true }); },
  filtros: [[N_('Novos'), r => r.c.de === 'cliente' && !r.c.lido]],
  vazio: N_('Recados entre você e o cliente, presos à pasta dele.'),
};

export const PORTAL: ModDef = { id: 'portal', colecoes: [cliente, documento, pedido, recado] };
void uid; void useState;
export type { Ctx };
